import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Space, Tooltip } from 'antd';
import moment from 'moment';

import {
  ACTIONS,
  U_CLIENT,
  U_EXECUTION_DATE,
  U_KEY,
  U_STATUS,
  U_TASK,
  U_TASK_TYPE,
} from '@app/constants';
import { TableTextComponent } from '@app/pages/TasksList/components';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import { shortName } from '@app/utils';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

const MAX_NAME_LENGTH = 28;
const MAX_TASK_NAME_LENGTH = 39;

const formatDateAndTime = (date: string, IsDeadlineAllDay?: boolean) => {
  const formattedDate = moment(date).format('DD.MM.YYYY');
  const formattedTime = IsDeadlineAllDay
    ? 'Весь день'
    : moment(date).format('HH:mm');
  return `${formattedDate} ${formattedTime}`;
};

// Task period start. For all-day deadlines the exact start time is not
// meaningful, so only the date is shown; otherwise date + time.
const formatStartDateAndTime = (date: string, IsDeadlineAllDay?: boolean) =>
  IsDeadlineAllDay
    ? moment(date).format('DD.MM.YYYY')
    : formatDateAndTime(date, false);

const getOwnersNames = (Owners: NCrmTasks.ModelUserNames[] | null) => {
  const ownerNames = Owners?.map((owner) => {
    const nameLast = owner.NameLast || '';
    const nameFirst = owner.NameFirst || '';
    return `${nameLast} ${nameFirst}`.trim();
  });

  const resultString = ownerNames?.join(', ') || '';
  return resultString;
};

interface AdaptDataToTableProps {
  data: NCrmTasks.ModelCrmTask[];
  handleEdit: (Id: number) => void;
  handlePreviewIconClick: (Id: number) => void;
  getRightToEditTask: (id?: number) => boolean;
}

export const adaptDataToTable = ({
  data,
  handleEdit,
  handlePreviewIconClick,
  getRightToEditTask,
}: AdaptDataToTableProps) => {
  return data.map(
    ({
      Id,
      TaskType,
      TaskTypeIconId,
      TaskTypeColor,
      Client,
      StatusDescription,
      IsDeadlineAllDay,
      TimeStart,
      TimeDeadline,
      StatusName,
      Description,
      Owners,
    }) => {
      return {
        [U_KEY]: String(Id),
        [U_EXECUTION_DATE]: (
          <div className="task-name" onClick={() => handlePreviewIconClick(Id)}>
            <TableTextComponent
              isStrong={true}
              text={formatDateAndTime(TimeDeadline, IsDeadlineAllDay)}
              status={StatusName}
            />
            <br />
            <TableTextComponent
              isStrong={false}
              text={`Начало: ${formatStartDateAndTime(
                TimeStart,
                IsDeadlineAllDay
              )}`}
              status={StatusName}
            />
            <br />
            <TableTextComponent
              isStrong={false}
              text={shortName(getOwnersNames(Owners), false, MAX_NAME_LENGTH)}
              status={StatusName}
            />
          </div>
        ),
        [U_CLIENT]: (
          <div>
            {Client ? (
              <TableTextComponent
                isStrong={true}
                text={shortName(
                  `Клиент: ${Client.NameLast || ''} ${
                    Client.NameFirst || ''
                  }`.trim(),
                  false,
                  MAX_NAME_LENGTH
                )}
                status={StatusName}
              />
            ) : (
              ''
            )}
          </div>
        ),
        [U_TASK_TYPE]: (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => handlePreviewIconClick(Id)}
          >
            {TaskTypeIconId && (
              <FontAwesomeIcon
                icon={TaskTypeIconId as IconProp}
                style={{ color: TaskTypeColor || undefined, flexShrink: 0 }}
              />
            )}
            <TableTextComponent
              isStrong={false}
              text={shortName(TaskType || '', false, MAX_TASK_NAME_LENGTH)}
              status={StatusName}
            />
          </div>
        ),
        [U_TASK]: (
          <div>
            {Description ? (
              <TableTextComponent
                isStrong={false}
                text={shortName(Description, false, MAX_TASK_NAME_LENGTH)}
                status={StatusName}
              />
            ) : (
              ''
            )}
          </div>
        ),
        [U_STATUS]: (
          <TableTextComponent
            isStrong={false}
            text={StatusDescription}
            status={StatusName}
          />
        ),
        [ACTIONS]: (
          <Space direction="horizontal">
            {getRightToEditTask(Id) ? (
              <Tooltip title="Редактировать">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(Id)}
                />
              </Tooltip>
            ) : (
              ''
            )}
            <Tooltip title="Просмотр задачи" placement="leftTop">
              <Button
                icon={<EyeOutlined />}
                onClick={() => handlePreviewIconClick(Id)}
              />
            </Tooltip>
          </Space>
        ),
      };
    }
  );
};
